import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './Footer.css';

const CONTACT_EMAIL = 'contact@rentnova.app';

export default function Footer() {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    // Smooth-scroll to an in-page section. If we're not on the landing page,
    // navigate there first, then scroll once the section mounts.
    const goToSection = (id: string) => (e: React.MouseEvent) => {
        e.preventDefault();
        const scroll = () => {
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        };
        if (location.pathname === '/') {
            scroll();
        } else {
            navigate('/');
            setTimeout(scroll, 60);
        }
    };

    return (
        <footer className="footer">
            <div className="container">
                <div className="footer__grid">
                    <div className="footer__brand">
                        <div className="footer__logo">
                            <img
                                src="/rentnova-mark.png"
                                alt="RentNova"
                                width={28}
                                height={28}
                                style={{ display: 'block', objectFit: 'contain', transform: 'translateY(-3px)' }}
                            />
                            <span>RentNova</span>
                        </div>
                        <p className="footer__tagline">
                            {t('footer.tagline')}
                        </p>
                        <a
                            href={`mailto:${CONTACT_EMAIL}`}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 8,
                                marginTop: '0.875rem',
                                padding: '0.5rem 0.75rem',
                                borderRadius: 10,
                                border: '1px solid rgba(126, 108, 192, 0.25)',
                                background: 'rgba(126, 108, 192, 0.06)',
                                color: '#4f46e5',
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                textDecoration: 'none',
                                transition: 'background 0.15s, border-color 0.15s',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(126, 108, 192, 0.12)';
                                e.currentTarget.style.borderColor = 'rgba(126, 108, 192, 0.45)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'rgba(126, 108, 192, 0.06)';
                                e.currentTarget.style.borderColor = 'rgba(126, 108, 192, 0.25)';
                            }}
                        >
                            <Mail size={15} />
                            {CONTACT_EMAIL}
                        </a>
                    </div>

                    <div className="footer__col">
                        <h4 className="footer__heading">{t('footer.product')}</h4>
                        <ul>
                            <li><a href="/#features" onClick={goToSection('features')}>{t('footer.features')}</a></li>
                            <li><a href="/#pricing" onClick={goToSection('pricing')}>{t('footer.pricing')}</a></li>
                            <li><a href="#">{t('footer.integrations')}</a></li>
                            <li><a href="#">{t('footer.changelog')}</a></li>
                        </ul>
                    </div>

                    <div className="footer__col">
                        <h4 className="footer__heading">{t('footer.company')}</h4>
                        <ul>
                            <li><a href="#">{t('footer.about')}</a></li>
                            <li><a href="#">{t('footer.blog')}</a></li>
                            <li><a href="#">{t('footer.careers')}</a></li>
                            <li><Link to="/contact">{t('footer.contactUs')}</Link></li>
                        </ul>
                    </div>

                    <div className="footer__col">
                        <h4 className="footer__heading">{t('footer.legal')}</h4>
                        <ul>
                            <li><Link to="/privacy">{t('footer.privacy')}</Link></li>
                            <li><Link to="/terms">{t('footer.terms')}</Link></li>
                            <li><Link to="/cookies">{t('footer.cookies')}</Link></li>
                            <li><Link to="/imprint">Imprint</Link></li>
                        </ul>
                    </div>
                </div>

                <div className="footer__bottom">
                    <p>{t('footer.copyright')}</p>
                </div>
            </div>
        </footer>
    );
}
